import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { InputEvent } from 'library/helpers/synthetic-events';
import withColor from 'util/hoc/with-color';
import { withA11y } from 'util/hoc';
import { handleKeyPress } from 'library/helpers/key-press';
import { BaseStyleguideColors } from 'context/color';

const StyledToggle = withA11y(
  withColor(styled.span`
  display: flex;
  width: 1.5em;
  height: 1em;
  border-radius: 1em;
  border: 1px solid #ccc;
  cursor: pointer;
  line-height: 1.5;
  transform: translateY(0.125em);
  ${(props) => !props.greenStyle && 'justify-content: flex-start;'}

  > span {
    display: inline-block;
    width: calc(1em - 2px);
    height: calc(1em - 2px);
    border-radius: 50%;
    background: ${(props) =>
      props.greenStyle ? BaseStyleguideColors.grey050 : props.colors.grey2};
    ${(props) =>
      props.greenStyle && 'margin-left: 0; transition: all 150ms ease-in-out;'}
  }

  &.active {
    ${(props) => !props.greenStyle && 'justify-content: flex-end;'}
    > span {
      ${(props) =>
        props.greenStyle &&
        'margin-left: 0.95em; transition: all 150ms ease-in-out;'}
      background: ${(props) =>
        props.greenStyle
          ? BaseStyleguideColors.green500
          : props.colors.primary};
    }
  }

  &:disabled,
  &.disabled,
  &[disabled] {
    background: ${(props) => BaseStyleguideColors.grey400};
    > span {
      background: ${(props) => BaseStyleguideColors.grey300};
      box-shadow: 0px 0px 0px;
    }
    cursor: not-allowed;
  }

  > input {
    border: none;
    padding: initial;
  }

  ${(props) =>
    props.greenStyle &&
    `
    background: ${
      props.checked
        ? BaseStyleguideColors.green100
        : BaseStyleguideColors.grey300
    };
    border: none;
    align-items: center;
    width: 2.2em;
    height: 0.85em;

    > span {
      height: 1.25em;
      width: 1.25em;
      ${
        !props.checked &&
        `box-shadow: 0px 2px 2px ${BaseStyleguideColors.grey400};`
      }
    }

    &:disabled,
    &.disabled,
    &[disabled] {
      cursor: not-allowed;
    }
  `}

  &:focus-within {
    ${(props) =>
      props.isTabbing
        ? `outline: #005FCC solid 2px; 
           outline-offset: 0.2em;`
        : 'outline: none;'}
  }
`)
);

class Toggle extends React.Component {
  handleChange = (e) => {
    const { disabled, onChange, checked, name } = this.props;
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      onChange(
        new InputEvent({
          value: !checked,
          name: name,
        })
      );
    }
  };

  render() {
    const {
      checked = false,
      disabled = false,
      className = '',
      startAt,
      name = '',
      label = '',
      ...props
    } = this.props;
    return (
      <StyledToggle
        className={[
          className,
          disabled ? 'disabled' : null,
          checked ? 'active' : null,
          'toggle',
        ]
          .filter((c) => c)
          .join(' ')}
        disabled={disabled}
        onClick={this.handleChange}
        name={name}
        checked={checked}
        {...props}
      >
        <input
          tabIndex={0}
          role="checkbox"
          checked={checked}
          autoFocus={name === startAt}
          onChange={this.handleChange}
          aria-label={`You are on a toggle to turn on the section ${name}. It is currently ${
            checked ? 'on' : 'off'
          }${disabled ? ' and disabled.' : '.'}`}
          onKeyUp={(e) => handleKeyPress(e, this.handleChange, true, true)}
          type="checkbox"
          style={{ opacity: 0, height: 0, width: 0, margin: 0 }}
        />
        <span />
      </StyledToggle>
    );
  }
}

Toggle.propTypes = {
  name: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  ref: PropTypes.any,
  startAt: PropTypes.string,
  label: PropTypes.string,
  greenStyle: PropTypes.bool,
};

export default Toggle;
